import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {Router, ActivatedRoute} from "@angular/router";
import {PageEvent} from "@angular/material/paginator";
import {TableService} from "../services/pager/pager";
import {CustomerFilter} from "./customer.filter";
import {WebService} from "../services/web/web.service";
import {SpinnerService} from "../services/spinner/spinner.service";
import {ToastService} from "../services/notification/toast.service";
import {SecurityService} from "../services/security.service";
import {TranslateService} from '@ngx-translate/core';
import {UserPermissionsService} from "../services/user-permissions.service";
import {Subject, TimeoutError} from 'rxjs';
import {finalize, takeUntil, timeout} from 'rxjs/operators';

type ContactType = "phone" | "email";

@Component({
    selector: "app-reports",
    templateUrl: "./reports.component.html",
    styleUrls: ["./reports.component.scss"],
    standalone: false
})
export class ReportsComponent implements OnInit, OnDestroy {
    // PARA MANEJO DE PANEL DE BUSQUEDA
    isOpenSearchPanel = false;
    filter = new CustomerFilter(this.tableService.filter);

    services: any[] = [];
    plansByService: any[] = [];
    selectedServices: any[] = [];
    selectedPlans: any[] = [];
    subscriptionStatus: string = 'all'; // 'all', 'active', 'inactive'
    showSubscriptionFilter: boolean = false;

    // PARA MANEJO DE TABLAS
    @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
    @ViewChild(MatSort) sort: MatSort | undefined;
    displayedColumns: string[] = [
        "name_customer",
        "lastname_customer",
        "identifier_customer",
        "code_customer",
        "contact",
        "name_plan",
        "actions",
    ];
    dataSource: any;
    customersLoading = false;
    servicesLoading = false;
    plansLoading = false;
    deleteLoading = false;
    private activeLoadingRequests = 0;
    private readonly reportRequestTimeoutMs = 20_000;
    private readonly destroy$ = new Subject<void>();

    constructor(
        public securityService: SecurityService,
        public tableService: TableService<any>,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private webService: WebService,
        private spinner: SpinnerService,
        private toast: ToastService,
        private translate: TranslateService,
        private userPermissionsService: UserPermissionsService
    ) {
        this.tableService = new TableService();
    }

    ngOnInit(): void {
        const restored = this.tableService.loadListState("reports-list");
        if (restored && restored.filter) {
            this.restoreForBack(restored);
        }

        this.isOpenSearchPanel = true;
        this.loadServices();
        this.list();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    restoreForBack(restored: any) {
        this.filter = new CustomerFilter(restored.filter);
        if (this.filter.services) {
            this.selectedServices = this.filter.services
                .split(",")
                .filter(Boolean)
                .map((idStr) => (isNaN(Number(idStr)) ? idStr : Number(idStr)));
        }
        if (this.filter.plans) {
            this.selectedPlans = this.filter.plans
                .split(",")
                .filter(Boolean)
                .map((idStr) => (isNaN(Number(idStr)) ? idStr : Number(idStr)));
        }
        if (this.filter.subscriptionStatus) {
            this.subscriptionStatus = this.filter.subscriptionStatus;
        }
        this.updateSubscriptionFilterVisibility();
        this.tableService.clearListState("reports-list");
        if (this.paginator) {
            this.paginator.pageIndex = restored.pageIndex;
            this.paginator.pageSize = restored.pageSize;
        }
    }

    loadServices(): void {
        if (this.servicesLoading) {
            return;
        }

        const partnerId = this.securityService.getTokenPartner();
        if (partnerId == undefined) {
            return this.toast.showInfo("No se puede cargar servicios porque no se encontró un partner.");
        }

        this.servicesLoading = true;
        this.startLoading();
        this.webService.get(`${this.webService.HOST}/partner/${partnerId}/services`).pipe(
            timeout(this.reportRequestTimeoutMs),
            takeUntil(this.destroy$),
            finalize(() => {
                this.servicesLoading = false;
                this.stopLoading();
            })
        ).subscribe(
            (response: any) => {
                this.services = response.result || [];
                // Sort services alphabetically by name_service
                this.services.sort((a: any, b: any) => {
                    if (a.name_service < b.name_service) {
                        return -1;
                    }
                    if (a.name_service > b.name_service) {
                        return 1;
                    }
                    return 0;
                });
            },
            (error) => {
                this.toast.showError("No se pudieron cargar los servicios. Intenta nuevamente.");
            },
        );
    }

    onPlanSelectionChange(selectedPlans: any[]): void {
        if (selectedPlans.includes("selectAll")) {
            if (this.selectedPlans.length - 1 === this.plansByService.length) {
                // Si todos los servicios ya están seleccionados, deselecciona todos
                this.selectedPlans = [];
            } else {
                // Si no todos los servicios están seleccionados, selecciona todos
                this.selectedPlans = this.plansByService.map((plan) => plan.id);
            }
        } else {
            this.selectedPlans = selectedPlans;
        }

        this.selectedPlans.sort((a: any, b: any) => {
            if (a.name_plan < b.name_plan) {
                return -1;
            }
            if (a.name_plan > b.name_plan) {
                return 1;
            }
            return 0;
        });

        this.updateSubscriptionFilterVisibility();
    }

    onServiceSelectionChange(selectedServices: any[]): void {
        if (selectedServices.includes("selectAll")) {
            if (this.selectedServices.length - 1 === this.services.length) {
                // Si todos los servicios ya están seleccionados, deselecciona todos
                this.selectedServices = [];
            } else {
                // Si no todos los servicios están seleccionados, selecciona todos
                this.selectedServices = this.services.map((service) => service.id);
            }
        } else {
            this.selectedServices = selectedServices;
        }

        this.selectedServices.sort((a: any, b: any) => {
            if (a.name_service < b.name_service) {
                return -1;
            }
            if (a.name_service > b.name_service) {
                return 1;
            }
            return 0;
        });

        this.loadPlansForSelectedServices();
        this.updateSubscriptionFilterVisibility();
    }

    loadPlansForSelectedServices(): void {
        if (this.selectedServices.length === 0) {
            this.plansByService = [];
            return;
        }

        if (this.plansLoading) {
            return;
        }

        this.plansLoading = true;
        this.startLoading();
        // const params = new HttpParams().set('list', this.selectedServices.join(','));
        this.webService
            .get(`${this.webService.HOST}/partner/services/plan?list=` + this.selectedServices.join(","))
            .pipe(
                timeout(this.reportRequestTimeoutMs),
                takeUntil(this.destroy$),
                finalize(() => {
                    this.plansLoading = false;
                    this.stopLoading();
                })
            )
            .subscribe(
                (response: any) => {
                    this.plansByService = response.result || [];
                    // Sort services alphabetically by name_service
                    this.plansByService.sort((a: any, b: any) => {
                        if (a.name_plan < b.name_plan) {
                            return -1;
                        }
                        if (a.name_plan > b.name_plan) {
                            return 1;
                        }
                        return 0;
                    });
                },
                (error) => {
                    this.toast.showError("No se pudieron cargar los planes. Intenta nuevamente.");
                },
            );
    }

    list(event?: PageEvent) {
        if (!this.canListCustomers()) {
            this.toast.showWarning("No tienes permisos para consultar clientes.");
            this.dataSource = [];
            return;
        }

        if (this.customersLoading) {
            return;
        }

        if (this.tableService.sort == undefined) {
            this.tableService.sort = "name_customer";
        }

        this.filter.services = this.selectedServices.join(",");
        this.filter.plans = this.selectedPlans.join(",");
        this.filter.subscriptionStatus = this.subscriptionStatus !== 'all' ? this.subscriptionStatus : undefined;

        let httpParams = this.webService.buildRequestParams(this.tableService.sort, "m", {
            pageIndex: event ? event.pageIndex : this.tableService.pager.pageIndex,
            pageSize: event ? event.pageSize : this.tableService.pager.pageSize,
        });

        httpParams = this.filter.getHttpParams(httpParams);
        if (this.securityService.getTokenPartner() == undefined) {
            return this.toast.showInfo("NO se puede cargar reporte de clientes porque NO posees datos de tu compañía, por favor crea una suscripcion para poder cargar tus datos en nuestro sistema.")
        }

        this.customersLoading = true;
        this.startLoading();
        this.webService
            .get(
                this.webService.HOST +
                "/partner/" +
                this.securityService.getTokenPartner() +
                "/customer",
                httpParams,
            )
            .pipe(
                timeout(this.reportRequestTimeoutMs),
                takeUntil(this.destroy$),
                finalize(() => {
                    this.customersLoading = false;
                    this.stopLoading();
                })
            )
            .subscribe(
                (response) => {
                    const result = response["result"] || [];
                    for (const r of result) {
                        r.status_customer = r.status_customer === 1 ? "Activo" : "Inactivo";
                        r.adress_customer = r.adress_customer === null ? "-" : r.adress_customer;
                    }

                    let customers = result;
                    customers.sort((a: any, b: any) => {
                        if (a.name_customer < b.name_customer) {
                            return -1;
                        }
                        if (a.name_customer > b.name_customer) {
                            return 1;
                        }
                        return 0;
                    });

                    this.dataSource = new MatTableDataSource<any>(customers);
                    this.tableService.pager = response["pager"];
                    this.tableService.selection.clear();
                },
                (error) => {
                    this.toast.showError("No se pudo cargar reporte de clientes. Intenta nuevamente.");
                },
            );
    }

    get reportLoading(): boolean {
        return this.customersLoading || this.servicesLoading || this.plansLoading || this.deleteLoading;
    }

    private startLoading(): void {
        if (this.activeLoadingRequests++ === 0) {
            this.spinner.open();
        }
    }

    private stopLoading(): void {
        if (this.activeLoadingRequests === 0) {
            return;
        }

        if (--this.activeLoadingRequests === 0) {
            this.spinner.close();
        }
    }

    search() {
        if (!this.canListCustomers()) {
            this.toast.showWarning("No tienes permisos para buscar clientes.");
            return;
        }

        this.tableService.filter = new CustomerFilter(this.filter);
        this.router.navigate([], {
            queryParams: {
                identifier_customer: this.filter.identifier_customer,
                code_customer: this.filter.code_customer,
                name_customer: this.filter.name_customer,
                lastname_customer: this.filter.lastname_customer,
                authorization_number: this.filter.authorization_number,
            },
        });
        this.list();
    }

    openSearchPanel(value: boolean) {
        this.isOpenSearchPanel = value;
        this.filter = new CustomerFilter(this.filter);
    }

    reset() {
        this.filter = new CustomerFilter();
        this.tableService.filter = null;
        this.dataSource = [];
        delete this.filter.name_customer;
        delete this.filter.code_customer;
        delete this.filter.identifier_customer;
        delete this.filter.lastname_customer;
        delete this.filter.authorization_number;
        this.selectedServices = [];
        this.selectedPlans = [];
        this.subscriptionStatus = 'all';
        this.showSubscriptionFilter = false;
        // this.isOpenSearchPanel = false;
        this.search();
    }

    applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    getContactValue(row: any, type: ContactType): string | null {
        const value = type === "phone" ? row?.phoneNumber_customer : row?.email_customer;
        if (value === null || value === undefined) {
            return null;
        }

        const contact = String(value);
        return contact.trim() ? contact : null;
    }

    update(item: any) {
        if (!this.canUpdateCustomer()) {
            this.toast.showWarning("No tienes permisos para actualizar clientes.");
            return;
        }

        // EDITAR
        this.memorizeForBack();
        this.router.navigate(["./" + item.id + "/update"], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: "preserve",
        });
    }

    plans(item: any) {
        if (!this.canViewCustomerPlans()) {
            this.toast.showWarning("No tienes permisos para consultar planes del cliente.");
            return;
        }

        this.memorizeForBack();
        this.router.navigate(["./" + item.id + "/plans"], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: "preserve",
        });
    }

    creditcard(item: any) {
        if (!this.canManageCustomerCards()) {
            this.toast.showWarning("No tienes permisos para consultar tarjetas del cliente.");
            return;
        }

        this.memorizeForBack();
        this.router.navigate(["./" + item.id + "/cards"], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: "preserve",
        });
    }

    invoices(item: any) {
        if (!this.canViewCustomerSubscriptions()) {
            this.toast.showWarning("No tienes permisos para consultar facturas del cliente.");
            return;
        }

        this.memorizeForBack();
        this.router.navigate(["./" + item.id + "/invoices"], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: "preserve",
        });
    }

    delete(item: any) {
        if (this.reportLoading) {
            return;
        }

        if (!item.canBeDeleted) {
            console.warn("⚠️ Intento de eliminar cliente no permitido - canBeDeleted es false");
            return;
        }

        if (confirm(`¿Está seguro que desea eliminar el cliente ${item.name_customer} ${item.lastName_customer}? (acción irreversible)`)) {
            const partnerId = this.securityService.getTokenPartner();

            const body = {
                customerIds: [item.id]
            };

            this.deleteLoading = true;
            this.startLoading();
            this.webService
                .post(body, `${this.webService.HOST}/partner/${partnerId}/customer/physical-delete`)
                .pipe(
                    timeout(this.reportRequestTimeoutMs),
                    takeUntil(this.destroy$),
                    finalize(() => {
                        this.deleteLoading = false;
                        this.stopLoading();
                    })
                )
                .subscribe(
                    (response) => {
                        this.toast.showSuccess("Cliente eliminado exitosamente");
                        this.list(); // Recargar la lista
                    },
                    (error) => {
                        if (error instanceof TimeoutError) {
                            this.toast.showError("No se pudo confirmar si el cliente fue eliminado. Verifica su estado antes de volver a intentarlo.");
                            this.list();
                            return;
                        }

                        this.toast.showError("No se pudo eliminar el cliente. Intenta nuevamente.");
                    }
                );
        } else {
        }
    }

    goBack() {
        this.router.navigate(["/my-portals"]);
    }

    memorizeForBack() {
        this.tableService.saveListState("reports-list", {
            filter: this.filter,
            pageIndex: this.paginator ? this.paginator.pageIndex : 0,
            pageSize: this.paginator ? this.paginator.pageSize : 10,
        });
    }

    updateSubscriptionFilterVisibility(): void {
        // Mostrar el filtro de suscripción si hay servicios seleccionados O planes seleccionados
        this.showSubscriptionFilter = this.selectedServices.length > 0 || this.selectedPlans.length > 0;

        // Si se oculta el filtro, resetear el valor a 'all'
        if (!this.showSubscriptionFilter) {
            this.subscriptionStatus = 'all';
            this.filter.subscriptionStatus = undefined;
        }
    }

    onSubscriptionStatusChange(status: string): void {
        this.subscriptionStatus = status;
        this.filter.subscriptionStatus = status !== 'all' ? status : undefined;
    }

    canListCustomers(): boolean {
        return this.userPermissionsService.hasPermission("controllers.Customers.findAllSearch");
    }

    canUpdateCustomer(): boolean {
        return this.userPermissionsService.hasPermission("controllers.Customers.update");
    }

    canViewCustomerPlans(): boolean {
        return this.userPermissionsService.hasAnyPermission([
            "controllers.HasPlans.findByPlan",
            "controllers.HasPlans.findAllCustomerHasPlanStatus",
            "controllers.HasPlans.findCustomerHasPlanStatusByType",
        ]);
    }

    canViewCustomerSubscriptions(): boolean {
        return this.userPermissionsService.hasAnyPermission([
            "controllers.HasPlans.returnSuscriptionsOfCustomer",
            "controllers.HasPlans.statusSuscriptionByID",
            "controllers.Invoices.listInvoicesReport",
        ]);
    }

    canManageCustomerCards(): boolean {
        return this.userPermissionsService.hasPermission("controllers.CreditCards.generateCreditcardToken");
    }

    canSeeCustomerActions(): boolean {
        return this.canListCustomers() || this.canUpdateCustomer() || this.canViewCustomerPlans() || this.canViewCustomerSubscriptions() || this.canManageCustomerCards();
    }
}
